import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from "react-helmet-async";
import HomePage from "./pages/HomePage";
const TopicDetailPage = lazy(() => import("./pages/TopicDetailPage"));
const TopicsPage = lazy(() => import("./pages/TopicsPage"));
const ControversiesPage = lazy(() => import("./pages/ControversiesPage"));
const SleepProfilePage = lazy(() => import("./pages/SleepProfilePage"));
const SleepJournalPage = lazy(() => import("./pages/SleepJournalPage"));
const SleepAssessmentPage = lazy(() => import("./pages/SleepAssessmentPage"));
const SleepReportPage = lazy(() => import("./pages/SleepReportPage"));
const WearableInsightsPage = lazy(() => import("./pages/WearableInsightsPage"));
const LabPortalPage = lazy(() => import("./pages/LabPortalPage"));
const DemoExperiencePage = lazy(() => import("./pages/DemoExperiencePage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ScriptManager = lazy(() => import("./pages/ScriptManager"));
const PremiumSeriesPage = lazy(() => import("./pages/PremiumSeriesPage"));
const CpsgtCoursePage = lazy(() => import("./pages/CpsgtCoursePage"));
const RpsgtCoursePage = lazy(() => import("./pages/RpsgtCoursePage"));
const CpsgtExamPage = lazy(() => import("./pages/CpsgtExamPage"));
const RpsgtExamPage = lazy(() => import("./pages/RpsgtExamPage"));
const PedsExamPage = lazy(() => import("./pages/PedsExamPage"));
const CcshExamPage = lazy(() => import("./pages/CcshExamPage"));

// Sprint 38 · Deep QA Pass F remediation — P0 admin route guard
import AdminGuard from "./components/AdminGuard";
// Sprint 38 · P1 Fix 3 — global expired-token → /education/login watcher
import EducationAuthWatcher from "./components/EducationAuthWatcher";

// Sleep Reset Academy
const SleepResetAcademyPage = lazy(() => import("./pages/SleepResetAcademyPage"));
const LearningPathPage = lazy(() => import("./pages/LearningPathPage"));

// Sleep Reset Academy — Phase 1.5 (Beginner/CPSGT/RPSGT structured paths)
const SleepResetAcademyHomePage = lazy(() => import("./pages/academy/SleepResetAcademyHomePage"));
const SleepResetPathPage = lazy(() => import("./pages/academy/SleepResetPathPage"));
const SleepResetModulePage = lazy(() => import("./pages/academy/SleepResetModulePage"));
const SleepResetLessonPage = lazy(() => import("./pages/academy/SleepResetLessonPage"));
const MultimodalLessonPage = lazy(() => import("./pages/academy/MultimodalLessonPage"));
const PresentationLessonPage = lazy(() => import("./pages/academy/PresentationLessonPage"));
const TrackPreviewPage = lazy(() => import("./pages/academy/TrackPreviewPage"));
import { preloadAcademyLessonMap } from "./pages/academy/lessonRoute";
// Sleep Reset Academy — Phase 2 (Dashboard, Profile, Certificates)
const SleepResetDashboardPage = lazy(() => import("./pages/academy/SleepResetDashboardPage"));
const SleepResetProfilePage = lazy(() => import("./pages/academy/SleepResetProfilePage"));
const SleepResetCertificatePage = lazy(() => import("./pages/academy/SleepResetCertificatePage"));
// Sleep Reset Academy — Phase 3 (Planner / Favorites / Search / Achievements / Transcript / Leaderboard / Settings)
const SleepResetPlannerPage = lazy(() => import("./pages/academy/SleepResetPlannerPage"));
const SleepResetFavoritesPage = lazy(() => import("./pages/academy/SleepResetFavoritesPage"));
const SleepResetSearchPage = lazy(() => import("./pages/academy/SleepResetSearchPage"));
const SleepResetAchievementsPage = lazy(() => import("./pages/academy/SleepResetAchievementsPage"));
const SleepResetTranscriptPage = lazy(() => import("./pages/academy/SleepResetTranscriptPage"));
const SleepResetLeaderboardPage = lazy(() => import("./pages/academy/SleepResetLeaderboardPage"));
const SleepResetSettingsPage = lazy(() => import("./pages/academy/SleepResetSettingsPage"));
// Sleep Reset Academy — Phase 4 (Portfolio + Calendar/Timeline)
const SleepResetPortfolioPage = lazy(() => import("./pages/academy/SleepResetPortfolioPage"));
const SleepResetCalendarPage = lazy(() => import("./pages/academy/SleepResetCalendarPage"));
// Sleep Reset Academy — Phase 5 (Assessment Ecosystem + Institutional Pages)
// Sprint 30.1 + Perf Remediation Gate 2026-03-02: SleepResetQuizPage lazy-loaded to keep the
// academyQuestions.generated.js bank out of the initial main.js chunk. webpackPrefetch keeps
// the shared question-bank chunk warm via browser idle-time prefetch after Academy first paint.
const SleepResetQuizPage = lazy(() => import(/* webpackChunkName: "academy-quiz" */ /* webpackPrefetch: true */ "./pages/academy/SleepResetQuizPage"));
const SleepResetInstitutionalPage = lazy(() => import("./pages/academy/SleepResetInstitutionalPage"));
// Sleep Reset Academy — Phase 5.5 (Ecosystem Consolidation)
// Sprint 30.1 + Perf Remediation Gate 2026-03-02: SleepResetProgramDirectorDashboardPage
// lazy-loaded (question-bank consumer). webpackPrefetch shares the "academy-quiz" chunk.
const SleepResetProgramDirectorDashboardPage = lazy(() => import(/* webpackChunkName: "academy-quiz" */ /* webpackPrefetch: true */ "./pages/academy/SleepResetProgramDirectorDashboardPage"));
const SleepResetSHSAIPage = lazy(() => import("./pages/academy/SleepResetSHSAIPage"));
const PatientEducationPage = lazy(() => import("./pages/patient/PatientEducationPage"));
// Sleep Reset Academy — Phase 6 (Professional Certification Ecosystem)
const SleepResetCertificationCenterPage = lazy(() => import("./pages/academy/SleepResetCertificationCenterPage"));
const SleepResetCertificationProgramPage = lazy(() => import("./pages/academy/SleepResetCertificationProgramPage"));
// Sleep Reset Academy — Phase 7 (Institutional Excellence)
// Sprint 30.1 + Perf Remediation Gate 2026-03-02: SleepResetPracticeCenterPage lazy-loaded
// (question-bank consumer). webpackPrefetch shares the "academy-quiz" chunk.
const SleepResetPracticeCenterPage = lazy(() => import(/* webpackChunkName: "academy-quiz" */ /* webpackPrefetch: true */ "./pages/academy/SleepResetPracticeCenterPage"));
// Sleep Reset Academy — Certification Mastery System (Feb 2026)
const SleepResetMasteryCoachPage = lazy(() => import("./pages/academy/SleepResetMasteryCoachPage"));
// Sprint 30.1 + Perf Remediation Gate 2026-03-02: SleepResetMasteryQuizPage lazy-loaded
// (question-bank consumer). webpackPrefetch shares the "academy-quiz" chunk.
const SleepResetMasteryQuizPage = lazy(() => import(/* webpackChunkName: "academy-quiz" */ /* webpackPrefetch: true */ "./pages/academy/SleepResetMasteryQuizPage"));

// Sprint 30.2 — Academy Identity Layer (AcademyShell). Wraps primary Sleep
// Reset Academy routes with a thin bilingual "SHS / Sleep Reset Academy"
// breadcrumb strip. Never wraps /academy/catalog or legacy Academy routes.
import AcademyShell from "./components/academy/AcademyShell";

// PWA Components
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { initCampaignTracking } from "./utils/campaignTracking";

// New Admin Panel Pages
const AdminHub = lazy(() => import("./pages/admin/AdminHub"));
const AdminTopicRevisionsPage = lazy(() => import("./pages/admin/AdminTopicRevisionsPage"));
const AdminReviewerQueuePage = lazy(() => import("./pages/admin/AdminReviewerQueuePage"));
const AdminSocialHubPage = lazy(() => import("./pages/admin/AdminSocialHubPage"));
const AdminMediaDeployPage = lazy(() => import("./pages/admin/AdminMediaDeployPage"));
const AdminAcademyModulePricingPage = lazy(() => import("./pages/admin/AdminAcademyModulePricingPage"));
const AdminPillarsPage = lazy(() => import("./pages/admin/AdminPillarsPage"));
const AdminTranscriptsPage = lazy(() => import("./pages/admin/AdminTranscriptsPage"));
const AdminTMKGPage = lazy(() => import("./pages/admin/AdminTMKGPage"));
const LivingDraftsProvenancePage = lazy(() => import("./pages/admin/LivingDraftsProvenancePage"));
const SipDashboardPage = lazy(() => import("./pages/admin/SipDashboardPage"));
const TrustIntelligencePage = lazy(() => import("./pages/TrustIntelligencePage"));
const TrustHubPage = lazy(() => import("./pages/TrustHubPage"));
const TrustMethodologyPage = lazy(() => import("./pages/trust/TrustMethodologyPage"));
const TrustSubProcessorsPage = lazy(() => import("./pages/trust/TrustSubProcessorsPage"));
const TrustCompliancePage = lazy(() => import("./pages/trust/TrustCompliancePage"));
const TrustSecurityPage = lazy(() => import("./pages/trust/TrustSecurityPage"));
const TrustAccreditationPage = lazy(() => import("./pages/trust/TrustAccreditationPage"));
const TrustPrivacyPage = lazy(() => import("./pages/trust/TrustPrivacyPage"));
const MyShsPage = lazy(() => import("./pages/MyShsPage"));
const MySleepPage = lazy(() => import("./pages/MySleepPage"));
const MySleepReferralIntakePage = lazy(() => import("./pages/MySleepReferralIntakePage"));
const MySleepSuppliesPage = lazy(() => import("./pages/MySleepSuppliesPage"));
const MySleepCoachPage = lazy(() => import("./pages/MySleepCoachPage"));
const MySleepWearableInsightsPage = lazy(() => import("./pages/MySleepWearableInsightsPage"));
const AdminAffiliatesPage = lazy(() => import("./pages/AdminAffiliatesPage"));
const AdminReferralsPage = lazy(() => import("./pages/AdminReferralsPage"));
const AdminReferralInboxPage = lazy(() => import("./pages/AdminReferralInboxPage"));
const FounderHomePage = lazy(() => import("./pages/admin/FounderHomePage"));
const BrainCommandCenterPage = lazy(() => import("./pages/admin/BrainCommandCenterPage"));
const LegacyExpiryReportPage = lazy(() => import("./pages/admin/LegacyExpiryReportPage"));
const ProposalDraftsPage = lazy(() => import("./pages/admin/ProposalDraftsPage"));
const DeviceQAAdminPage = lazy(() => import("./pages/admin/DeviceQAAdminPage"));
const DeviceQATesterPage = lazy(() => import("./pages/devqa/DeviceQATesterPage"));
const MyPlanPage = lazy(() => import("./pages/MyPlanPage"));
import AdminNav from "./components/admin/AdminNav";
const FounderUploadsPage = lazy(() => import("./pages/admin/FounderUploadsPage"));
const WebsiteSectionsPage = lazy(() => import("./pages/admin/WebsiteSectionsPage"));
const MembershipsPage = lazy(() => import("./pages/admin/ConsoleReportPages").then((m) => ({ default: m.MembershipsPage })));
const ActivityPage = lazy(() => import("./pages/admin/ConsoleReportPages").then((m) => ({ default: m.ActivityPage })));
const AdvancedToolsPage = lazy(() => import("./pages/admin/ConsoleReportPages").then((m) => ({ default: m.AdvancedToolsPage })));
const AcademyOverviewPage = lazy(() => import("./pages/admin/ConsoleInventoryPages").then((m) => ({ default: m.AcademyOverviewPage })));
const ApprovalsPage = lazy(() => import("./pages/admin/ConsoleInventoryPages").then((m) => ({ default: m.ApprovalsPage })));
import AdminEntryRedirect from "./components/admin/console/AdminEntryRedirect";
const MyLearningPathPage = lazy(() => import("./pages/MyLearningPathPage"));
const MyDailyBriefPage = lazy(() => import("./pages/MyDailyBriefPage"));
const MyWearablesPage = lazy(() => import("./pages/MyWearablesPage"));
const EnterprisePortalPage = lazy(() => import("./pages/EnterprisePortalPage"));
const EnterpriseDocPage = lazy(() => import("./pages/EnterpriseDocPage"));
const SleepCoachPage = lazy(() => import("./pages/SleepCoachPage"));
const PillarPage = lazy(() => import("./pages/PillarPage"));
const PillarsIndexPage = lazy(() => import("./pages/PillarsIndexPage"));
const VideoProductionDashboard = lazy(() => import("./pages/VideoProductionDashboard"));
const AdminVideos = lazy(() => import("./pages/admin/AdminVideos"));
const AdminAcademy = lazy(() => import("./pages/admin/AdminAcademy"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const BulkVideoImport = lazy(() => import("./pages/admin/BulkVideoImport"));
const AdminEnterprise = lazy(() => import("./pages/admin/AdminEnterprise"));
const AdminResearch = lazy(() => import("./pages/admin/AdminResearch"));
const AdminSeries = lazy(() => import("./pages/admin/AdminSeries"));
const AdminScoringMastery = lazy(() => import("./pages/admin/AdminScoringMastery"));
const AdminDBHealthPage = lazy(() => import("./pages/admin/AdminDBHealthPage"));
const BilingualReachPage = lazy(() => import("./pages/admin/BilingualReachPage"));
const InstitutionalLeadsPage = lazy(() => import("./pages/admin/InstitutionalLeadsPage"));
const AdminInstitutionalLicensingPage = lazy(() => import("./pages/admin/AdminInstitutionalLicensingPage"));
const OrgLicensesPage = lazy(() => import("./pages/institutional/OrgLicensesPage"));
const InstitutionalPricingPreviewPage = lazy(() => import("./pages/institutional/InstitutionalPricingPreviewPage"));
const InstitutionalEducationPage = lazy(() => import("./pages/institutional/InstitutionalEducationPage"));
const MembershipPlansPage = lazy(() => import("./pages/MembershipPlansPage"));
const OrganizationalLeadsPage = lazy(() => import("./pages/admin/OrganizationalLeadsPage"));
const ClinicsPrivatePricingPage = lazy(() => import("./pages/clinic/ClinicsPrivatePricingPage"));
const OrganizationalPricingPage = lazy(() => import("./pages/organizational/OrganizationalPricingPage"));
const PrivacyPolicyV2Page = lazy(() => import("./pages/legal/PrivacyPolicyV2Page"));
const SubProcessorsPage = lazy(() => import("./pages/legal/SubProcessorsPage"));

// Phase B · Post-Gate-0C Expansion Program (W3/W4/W5)
const InstitutionalVideosPage = lazy(() => import("./pages/InstitutionalVideosPage"));
const AdminVideoStudioPage = lazy(() => import("./pages/admin/AdminVideoStudioPage"));
const AdminPhaseD1ReviewPage = lazy(() => import("./pages/admin/AdminPhaseD1ReviewPage"));
const AdminPermanentLibraryPage = lazy(() => import("./pages/admin/AdminPermanentLibraryPage"));
const AdminFounderLibraryPage = lazy(() => import("./pages/admin/AdminFounderLibraryPage"));
const AdminDubPilotPage = lazy(() => import("./pages/admin/AdminDubPilotPage"));
const AdminSocialPublishingPage = lazy(() => import("./pages/admin/AdminSocialPublishingPage"));
const AdminV1ParityPage = lazy(() => import("./pages/admin/AdminV1ParityPage"));
const AdminFounderAssetsPage = lazy(() => import("./pages/admin/AdminFounderAssetsPage"));
const AdminInstitutionalVideosPage = lazy(() => import("./pages/admin/AdminInstitutionalVideosPage"));
const AdminCaptionReviewPage = lazy(() => import("./pages/admin/AdminCaptionReviewPage"));
const AdminInstitutionalClassifierPage = lazy(() => import("./pages/admin/AdminInstitutionalClassifierPage"));

// SHS AI Growth Engine (Admin Marketing)
const GrowthEnginePage = lazy(() => import("./pages/GrowthEnginePage"));

// Revenue Dashboard (Executive Analytics)
const RevenueDashboardPage = lazy(() => import("./pages/RevenueDashboardPage"));

// Organizations (B2B)
const OrganizationsPage = lazy(() => import("./pages/OrganizationsPage"));

// Research Program
const ResearchPage = lazy(() => import("./pages/ResearchPage"));

// B2B Clinic Pages
const ClinicsLandingPage = lazy(() => import("./pages/clinic/ClinicsLandingPage"));
const ClinicsPricingPage = lazy(() => import("./pages/clinic/ClinicsPricingPage"));
const ClinicsLoginPage = lazy(() => import("./pages/clinic/ClinicsLoginPage"));
const ClinicsRegisterPage = lazy(() => import("./pages/clinic/ClinicsRegisterPage"));
const ClinicPortalPage = lazy(() => import("./pages/clinic/ClinicPortalPage"));
const TeamManagementPage = lazy(() => import("./pages/clinic/TeamManagementPage"));

// Patient Intake Pages
const IntakeWelcomePage = lazy(() => import("./pages/intake/IntakeWelcomePage"));
const IntakeConsentPage = lazy(() => import("./pages/intake/IntakeConsentPage"));
const IntakeDemographicsPage = lazy(() => import("./pages/intake/IntakeDemographicsPage"));
const IntakeQuestionnairesPage = lazy(() => import("./pages/intake/IntakeQuestionnairesPage"));
const IntakeResultsPage = lazy(() => import("./pages/intake/IntakeResultsPage"));
import { IntakeProvider } from "./contexts/IntakeContext";

// Report Assistant Pages
const ReportDashboardPage = lazy(() => import("./pages/report-assistant/ReportDashboardPage"));
const NewDraftPage = lazy(() => import("./pages/report-assistant/NewDraftPage"));
const DraftDetailPage = lazy(() => import("./pages/report-assistant/DraftDetailPage"));
const TemplateManagementPage = lazy(() => import("./pages/report-assistant/TemplateManagementPage"));
import { ReportProvider } from "./contexts/ReportContext";

// Education & Academy Pages (Native Membership - replaces Patreon)
const AcademyPage = lazy(() => import("./pages/AcademyPage"));
const AcademyLandingPage = lazy(() => import("./pages/AcademyLandingPage"));
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const LessonPage = lazy(() => import("./pages/LessonPage"));
const LegacyAcademyWrapper = lazy(() => import("./pages/legacy/LegacyAcademyWrapper"));
const EducationGoogleAuthCallback = lazy(() => import("./pages/education/EducationGoogleAuthCallback"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const AuthorityPage = lazy(() => import("./pages/AuthorityPage"));
const QuestionBanksPage = lazy(() => import("./pages/QuestionBanksPage"));
const AIAssistantPage = lazy(() => import("./pages/AIAssistantPage"));
const CertificatesPage = lazy(() => import("./pages/CertificatesPage"));
const VerifyCertificatePage = lazy(() => import("./pages/VerifyCertificatePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const EducationLoginPage = lazy(() => import("./pages/education/EducationLoginPage"));
const EducationRegisterPage = lazy(() => import("./pages/education/EducationRegisterPage"));
import { EducationAuthProvider } from "./contexts/EducationAuthContext";
import { AcademyProvider } from "./contexts/AcademyContext";

// Strategic Concept Pages (Unlisted)
const ScoringMasteryPage = lazy(() => import("./pages/ScoringMasteryPage"));
const CbtiCoachPage = lazy(() => import("./pages/CbtiCoachPage"));
const DirectorDashboardPage = lazy(() => import("./pages/DirectorDashboardPage"));

// Password Reset
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"));
const EmailVerifyPage = lazy(() => import("./pages/education/EmailVerifyPage"));

// Tech Pocket Guide™
const TechPocketGuideLandingPage = lazy(() => import("./pages/TechPocketGuideLandingPage"));
const TechPocketGuideApp = lazy(() => import("./pages/TechPocketGuideApp"));

// Scoring Mastery™ MVP (Respiratory Gold Standard)
const ScoringMasteryAppPage = lazy(() => import("./pages/ScoringMasteryAppPage"));
const ScoringMasteryExercisePage = lazy(() => import("./pages/ScoringMasteryExercisePage"));
const ScoringMasteryAdminPage = lazy(() => import("./pages/ScoringMasteryAdminPage"));
const ScoringMasteryQADashboardPage = lazy(() => import("./pages/ScoringMasteryQADashboardPage"));

// SHS Scoring Simulator™
const ScoringSimulatorPage = lazy(() => import("./pages/ScoringSimulatorPage"));

// SHS Sleep Trends & Insights™
const InsightsDashboardPage = lazy(() => import("./pages/InsightsDashboardPage"));

// CPAP Fit Assistant™
const CPAPFitAssistantPage = lazy(() => import("./pages/CPAPFitAssistantPage"));
const CPAPAnalyticsPage = lazy(() => import("./pages/CPAPAnalyticsPage"));

// CPAP Adherence Engine™
const CPAPAdherencePage = lazy(() => import("./pages/CPAPAdherencePage"));
const CPAPSuccessStoriesPage = lazy(() => import("./pages/CPAPSuccessStoriesPage"));

// ITER186 · Admin governance for CPAP Success Stories
const AdminCPAPStoriesPage = lazy(() => import("./pages/admin/AdminCPAPStoriesPage"));
// POST-ITER186 · Admin Ecosystem Command Center (read-only overview)
const AdminEcosystemPage = lazy(() => import("./pages/admin/AdminEcosystemPage"));

// Payment Pages
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));

// Ad Campaign Manager
const CampaignDashboard = lazy(() => import("./pages/CampaignDashboard"));

// Video Script Generator
const VideoScriptGeneratorPage = lazy(() => import("./pages/VideoScriptGeneratorPage"));

// Growth Command Center (Admin)
const GrowthCommandCenterPage = lazy(() => import("./pages/GrowthCommandCenterPage"));

// CEO Dashboard (Admin)
const AdminGrowthDashboard = lazy(() => import("./pages/AdminGrowthDashboard"));

// Content Protection Admin
const ContentProtectionAdminPage = lazy(() => import("./pages/ContentProtectionAdminPage"));

// Interactive Academy Modules
const InteractiveModulePage = lazy(() => import("./pages/InteractiveModulePage"));

// Sleep Reset Coach™ (Standalone Product)
const CoachDashboardPage = lazy(() => import("./pages/CoachDashboardPage"));

import Header from "./components/Header";
import Footer from "./components/Footer";
import AIHelper from "./components/AIHelper";
import HelpCenterLite from "./components/HelpCenterLite";
import FloatingAudioPlayer from "./components/FloatingAudioPlayer";
import MobileAssistantHub from "./components/MobileAssistantHub";
import CookieNoticeBanner from "./components/CookieNoticeBanner";
import { GuestDataPrompt } from "./components/GuestDataPrompt";
import { FounderPreviewBanner } from "./components/FounderPreviewBanner";
const FounderPreviewPage = lazy(() => import("./pages/admin/FounderPreviewPage"));
const AdminReviewPage = lazy(() => import("./pages/admin/AdminReviewPage"));
const SecurityReadinessPage = lazy(() => import("./pages/admin/SecurityReadinessPage"));
const PublicationReconcilePage = lazy(() => import("./pages/admin/PublicationReconcilePage"));
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { AIChatProvider } from "./contexts/AIChatContext";
import { ClinicAuthProvider } from "./contexts/ClinicAuthContext";
import { AudioPlayerProvider } from "./contexts/AudioPlayerContext";
// SHS Visual Experience System — Sprint 29.1A (accessibility & infrastructure foundation).
// MotionModeProvider must live inside <BrowserRouter/> because it consumes
// useLocation(); it is placed as the outermost non-router provider so any
// business-logic context can consume it without extra plumbing. It writes
// data-experience-mode on <html> and has no visual effect in 29.1A.
import { MotionModeProvider } from "./contexts/MotionModeContext";

// Remount lesson players per topic so state never carries over between lessons.
const KeyedByTopic = ({ Component }) => {
  const { topicId } = useParams();
  return <Component key={topicId} />;
};

// ITER177 · Emergent-managed Google Auth callback gate.
// Detected SYNCHRONOUSLY during render via useLocation().hash — a useEffect
// would run after the first render, too late for the ProtectedRoute race.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
function SkipLinkLabel() {
  const { language } = useLanguage();
  return language === "es" ? "Saltar al contenido principal" : "Skip to main content";
}

// Catch-all: a missing route on production usually means a stale cached bundle right after a deploy.
function CatchAllNotFound() {
  const { language } = useLanguage();
  const es = language === "es";
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="app-catchall-404">
        <p className="font-semibold text-amber-950">{es ? "Página no encontrada" : "Page not found"}</p>
        <p className="mt-2 text-xs">{es
          ? "Esta dirección no existe o su navegador conserva una versión anterior del sitio. Pruebe a recargar la página (Ctrl+Shift+R / Cmd+Shift+R)."
          : "This address does not exist, or your browser still holds an older version of the site. Try reloading the page (Ctrl+Shift+R / Cmd+Shift+R)."}</p>
        <a href="/" className="mt-3 inline-block underline text-amber-900 font-medium" data-testid="app-catchall-home">{es ? "Ir al inicio" : "Go to home"}</a>
        <p className="mt-2 text-[10px] font-mono text-amber-700">{typeof window !== "undefined" ? window.location.pathname : ""}</p>
      </div>
    </div>
  );
}

// Friendly aliases for common URLs; keeps ?query (e.g. reset tokens) and #hash.
function KeepQueryRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

function GoogleAuthGate({ children }) {
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <Suspense fallback={<div role="status" aria-live="polite" className="sr-only">Loading</div>}><EducationGoogleAuthCallback /></Suspense>;
  }
  return children;
}

function App() {
  // Initialize campaign tracking on app load
  useEffect(() => {
    initCampaignTracking();
    // A0 — Warm the canonical lesson-map cache once so premium routing
    // is authoritative from the first render onward.
    preloadAcademyLessonMap();
  }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <MotionModeProvider>
          <LanguageProvider>
            <AIChatProvider>
              <ClinicAuthProvider>
                <EducationAuthProvider>
                  <AcademyProvider>
                    <AudioPlayerProvider>
                    {/* WCAG 2.1 — keyboard-only "Skip to main content" link.
                        Visually hidden until focused; jumps directly to <main>. */}
                    <a
                      href="#main-content"
                      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-blue-700 focus:text-white focus:rounded focus:outline-none focus:ring-2 focus:ring-white"
                      data-testid="skip-to-main"
                    >
                      <SkipLinkLabel />
                    </a>
                    <Header />
                    {/* Sprint 38 · P1 Fix 3 — mounts the global watcher for
                        `shs:auth-expired` events (see components/EducationAuthWatcher).
                        Redirects to /education/login preserving state.from. */}
                    <EducationAuthWatcher />
                    <main id="main-content" role="main" tabIndex={-1}>
                    {/* Sprint 30.1 — Suspense boundary required by the four
                        lazy-loaded Academy question-bank consumer routes
                        (SleepResetQuizPage, SleepResetMasteryQuizPage,
                        SleepResetPracticeCenterPage,
                        SleepResetProgramDirectorDashboardPage). Statically
                        imported routes are unaffected; fallback is visually
                        hidden and announced to assistive tech only. */}
                    <Suspense fallback={<div role="status" aria-live="polite" className="sr-only">Loading</div>}>
                    <GoogleAuthGate>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/topics" element={<TopicsPage />} />
                      <Route path="/controversies" element={<ControversiesPage />} />
                      <Route path="/topic/:id" element={<TopicDetailPage />} />
                      <Route path="/sleep-profile" element={<SleepProfilePage />} />
                      <Route path="/sleep-journal" element={<SleepJournalPage />} />
                      <Route path="/sleep-assessment" element={<SleepAssessmentPage />} />
                      <Route path="/sleep-report" element={<SleepReportPage />} />
                      <Route path="/wearable-insights" element={<WearableInsightsPage />} />
                      <Route path="/lab-portal" element={<LabPortalPage />} />
                      <Route path="/lab-demo" element={<DemoExperiencePage />} />
                      <Route path="/premium-series" element={<PremiumSeriesPage />} />
                      <Route path="/trust" element={<TrustHubPage />} />
                      <Route path="/trust/intelligence" element={<TrustIntelligencePage />} />
                      <Route path="/trust/methodology" element={<TrustMethodologyPage />} />
                      <Route path="/trust/sub-processors" element={<TrustSubProcessorsPage />} />
                      <Route path="/trust/compliance" element={<TrustCompliancePage />} />
                      <Route path="/trust/security" element={<TrustSecurityPage />} />
                      <Route path="/trust/accreditation" element={<TrustAccreditationPage />} />
                      <Route path="/trust/privacy" element={<TrustPrivacyPage />} />
                      <Route path="/my-shs" element={<MyShsPage />} />
                      <Route path="/my-shs/learning" element={<MyLearningPathPage />} />
                      <Route path="/my-shs/brief" element={<MyDailyBriefPage />} />
                      <Route path="/my-shs/wearables" element={<MyWearablesPage />} />
                      {/* Gate B · My Sleep / Mi Sueño patient shell + referral intake */}
                      <Route path="/mi-sueno" element={<MySleepPage />} />
                      <Route path="/my-sleep" element={<MySleepPage />} />
                      <Route path="/mi-sueno/referral" element={<MySleepReferralIntakePage />} />
                      <Route path="/my-sleep/referral" element={<MySleepReferralIntakePage />} />
                      <Route path="/mi-sueno/supplies" element={<MySleepSuppliesPage />} />
                      <Route path="/my-sleep/supplies" element={<MySleepSuppliesPage />} />
                      <Route path="/mi-sueno/coach" element={<MySleepCoachPage />} />
                      <Route path="/my-sleep/coach" element={<MySleepCoachPage />} />
                      <Route path="/mi-sueno/wearable-insights" element={<MySleepWearableInsightsPage />} />
                      <Route path="/my-sleep/wearable-insights" element={<MySleepWearableInsightsPage />} />
                      {/* Gate C · Affiliate Sleep Center directory (Super Admin only) */}
                      <Route path="/admin/affiliates" element={<AdminGuard><AdminNav current="labs"><AdminAffiliatesPage /></AdminNav></AdminGuard>} />
                      {/* Gate D · My Sleep referral matching review (Super Admin only) */}
                      <Route path="/admin/mysleep-referrals" element={<AdminGuard><AdminNav current="referrals"><AdminReferralInboxPage /></AdminNav></AdminGuard>} />
                      <Route path="/admin/mysleep-referrals/matching" element={<AdminGuard><AdminReferralsPage /></AdminGuard>} />
                      <Route path="/enterprise" element={<EnterprisePortalPage />} />
                      <Route path="/enterprise/:slug" element={<EnterpriseDocPage />} />
                      
                      {/* Education & Membership Routes (Native - replaces Patreon) */}
                      <Route path="/membership" element={<MembershipPlansPage />} />
                      <Route path="/my-plan" element={<MyPlanPage />} />
                      <Route path="/shs-membership" element={<MembershipPlansPage />} />
                      <Route path="/education/login" element={<EducationLoginPage />} />
                      <Route path="/education/register" element={<EducationRegisterPage />} />
                      <Route path="/education/reset-password" element={<PasswordResetPage />} />
                      <Route path="/education/verify-email" element={<EmailVerifyPage />} />
                      <Route path="/dashboard" element={<StudentDashboard />} />
                      <Route path="/authority" element={<AuthorityPage />} />
                      {/* ITER175 Academy Consolidation — /academy is now the canonical Sleep Reset Academy.
                          Legacy AcademyLandingPage / AcademyPage / CourseDetailPage / LessonPage retired to a
                          _legacy prefix so their unique content stays reachable if we ever need it, while
                          every public Academy entry point resolves into the canonical dark Academy shell. */}
                      <Route path="/academy" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/academy/catalog" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/academy/course/:courseId" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/academy/lesson/:lessonId" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/_legacy/academy" element={<LegacyAcademyWrapper label="AcademyLandingPage"><AcademyShell><AcademyLandingPage /></AcademyShell></LegacyAcademyWrapper>} />
                      <Route path="/_legacy/academy/catalog" element={<LegacyAcademyWrapper label="AcademyPage"><AcademyPage /></LegacyAcademyWrapper>} />
                      <Route path="/_legacy/academy/course/:courseId" element={<LegacyAcademyWrapper label="CourseDetailPage"><CourseDetailPage /></LegacyAcademyWrapper>} />
                      <Route path="/_legacy/academy/lesson/:lessonId" element={<LegacyAcademyWrapper label="LessonPage"><LessonPage /></LegacyAcademyWrapper>} />
                      <Route path="/academy/certificates" element={<CertificatesPage />} />
                      <Route path="/academy/interactive" element={<InteractiveModulePage />} />
                      <Route path="/academy/interactive/:moduleId" element={<InteractiveModulePage />} />
                      <Route path="/certificates" element={<CertificatesPage />} />
                      <Route path="/question-banks" element={<QuestionBanksPage />} />
                      <Route path="/ai" element={<AIAssistantPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/videos" element={<VideosPage />} />
                      <Route path="/verify-certificate/:certificateId" element={<VerifyCertificatePage />} />
                      <Route path="/verify-certificate" element={<VerifyCertificatePage />} />
                      
                      {/* Sleep Reset Coach™ Routes */}
                      <Route path="/coach" element={<CoachDashboardPage />} />
                      
                      {/* Director Dashboard / Lab Management */}
                      <Route path="/director" element={<DirectorDashboardPage />} />
                      <Route path="/lab-dashboard" element={<DirectorDashboardPage />} />
                      
                      {/* Legacy routes - redirect to new paths */}
                      <Route path="/patreon-tiers" element={<KeepQueryRedirect to="/membership" />} />
                      <Route path="/academy/cpsgt" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/academy/rpsgt" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      
                      {/* ITER175: /sleep-academy + /academy/learn both consolidate into canonical Sleep Reset Academy. */}
                      <Route path="/sleep-academy" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/sleep-academy/*" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/academy/learn" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/_legacy/academy/learn" element={<LegacyAcademyWrapper label="SleepResetAcademyPage"><SleepResetAcademyPage /></LegacyAcademyWrapper>} />

                      {/* Sprint 30.2 — Sleep Reset Academy primary routes wrapped with <AcademyShell />.
                          Provides "SHS / Sleep Reset Academy" identity strip + bilingual breadcrumb.
                          NOT applied to /academy/catalog or any legacy DB-backed Academy route. */}

                      {/* Sleep Reset Academy Phase 1.5 — Structured Module/Path/Lesson UI (bilingual) */}
                      <Route path="/academy/sleep-reset" element={<AcademyShell><SleepResetAcademyHomePage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/path/:pathKey" element={<AcademyShell><SleepResetPathPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/module/:moduleId" element={<AcademyShell><SleepResetModulePage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/lesson/:topicId" element={<AcademyShell><SleepResetLessonPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/multimodal/:topicId" element={<KeyedByTopic Component={MultimodalLessonPage} />} />
                      <Route path="/academy/sleep-reset/presentation/:topicId" element={<KeyedByTopic Component={PresentationLessonPage} />} />
                      <Route path="/academy/sleep-reset/tracks" element={<TrackPreviewPage />} />
                      <Route path="/academy/tracks" element={<TrackPreviewPage />} />
                      {/* Phase 2 — Dashboard / Profile / Certificate */}
                      <Route path="/academy/sleep-reset/dashboard" element={<AcademyShell><SleepResetDashboardPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/profile" element={<AcademyShell><SleepResetProfilePage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/certificate/:certId" element={<AcademyShell><SleepResetCertificatePage /></AcademyShell>} />
                      {/* Phase 3 — Planner / Favorites / Search / Achievements / Transcript / Leaderboard / Settings */}
                      <Route path="/academy/sleep-reset/planner" element={<AcademyShell><SleepResetPlannerPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/favorites" element={<AcademyShell><SleepResetFavoritesPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/search" element={<AcademyShell><SleepResetSearchPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/achievements" element={<AcademyShell><SleepResetAchievementsPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/transcript" element={<AcademyShell><SleepResetTranscriptPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/leaderboard" element={<AcademyShell><SleepResetLeaderboardPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/settings" element={<AcademyShell><SleepResetSettingsPage /></AcademyShell>} />
                      {/* Phase 4 — Portfolio + Calendar/Timeline */}
                      <Route path="/academy/sleep-reset/portfolio" element={<AcademyShell><SleepResetPortfolioPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/calendar" element={<AcademyShell><SleepResetCalendarPage /></AcademyShell>} />
                      {/* Phase 5 — Assessment Ecosystem + Institutional Pages */}
                      <Route path="/academy/sleep-reset/quiz/:scope/:scopeKey" element={<AcademyShell><SleepResetQuizPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/about/:slug" element={<AcademyShell><SleepResetInstitutionalPage /></AcademyShell>} />
                      {/* Phase 5.5 — Ecosystem Consolidation */}
                      <Route path="/academy/sleep-reset/program-director-dashboard" element={<AcademyShell><SleepResetProgramDirectorDashboardPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/shs-ai" element={<AcademyShell><SleepResetSHSAIPage /></AcademyShell>} />
                      <Route path="/patient-education" element={<PatientEducationPage />} />
                      {/* Phase 6 — Professional Certification Ecosystem */}
                      <Route path="/academy/sleep-reset/certifications" element={<AcademyShell><SleepResetCertificationCenterPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/certifications/:programKey" element={<AcademyShell><SleepResetCertificationProgramPage /></AcademyShell>} />
                      {/* Phase 7 — Institutional Excellence */}
                      <Route path="/academy/sleep-reset/practice" element={<AcademyShell><SleepResetPracticeCenterPage /></AcademyShell>} />
                      {/* Certification Mastery System */}
                      <Route path="/academy/sleep-reset/mastery/:programKey" element={<AcademyShell><SleepResetMasteryCoachPage /></AcademyShell>} />
                      <Route path="/academy/sleep-reset/mastery/:programKey/:mode" element={<AcademyShell><SleepResetMasteryQuizPage /></AcademyShell>} />
                      
                      {/* Exam Routes (still available) */}
                      <Route path="/exam/cpsgt" element={<CpsgtExamPage />} />
                      <Route path="/exam/rpsgt" element={<RpsgtExamPage />} />
                      <Route path="/exam/peds" element={<PedsExamPage />} />
                      <Route path="/exam/ccsh" element={<CcshExamPage />} />
                      
                      {/* Learning Path - Academy → Exams → Certification */}
                      <Route path="/learning-path" element={<LearningPathPage />} />
                      
                      {/* Admin Routes */}
                      <Route path="/admin" element={<AdminEntryRedirect />} />
                      <Route path="/admin/legacy-login" element={<AdminLoginPage />} />
                      <Route path="/admin/home" element={<AdminGuard><FounderHomePage /></AdminGuard>} />
                      <Route path="/admin/brain" element={<AdminGuard><BrainCommandCenterPage /></AdminGuard>} />
                      <Route path="/admin/proposals" element={<AdminGuard><ProposalDraftsPage /></AdminGuard>} />
                      <Route path="/admin/device-qa" element={<AdminGuard><DeviceQAAdminPage /></AdminGuard>} />
                      <Route path="/admin/legacy-expiry" element={<AdminGuard><LegacyExpiryReportPage /></AdminGuard>} />
                      <Route path="/admin/founder-preview" element={<AdminGuard><FounderPreviewPage /></AdminGuard>} />
                      <Route path="/admin/review-grants" element={<AdminGuard><AdminReviewPage /></AdminGuard>} />
                      <Route path="/admin/security-readiness" element={<AdminGuard><SecurityReadinessPage /></AdminGuard>} />
                      <Route path="/admin/publication-reconcile" element={<AdminGuard><PublicationReconcilePage /></AdminGuard>} />
                      <Route path="/device-qa" element={<DeviceQATesterPage />} />
                      <Route path="/admin/uploads" element={<AdminGuard><FounderUploadsPage /></AdminGuard>} />
                      <Route path="/admin/website" element={<AdminGuard><WebsiteSectionsPage /></AdminGuard>} />
                      <Route path="/admin/memberships" element={<AdminGuard><MembershipsPage /></AdminGuard>} />
                      <Route path="/admin/activity" element={<AdminGuard><ActivityPage /></AdminGuard>} />
                      <Route path="/admin/advanced" element={<AdminGuard><AdvancedToolsPage /></AdminGuard>} />
                      <Route path="/admin/academy-overview" element={<AdminGuard><AcademyOverviewPage /></AdminGuard>} />
                      <Route path="/admin/approvals" element={<AdminGuard><ApprovalsPage /></AdminGuard>} />
                      <Route path="/admin/dashboard" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
                      <Route path="/admin/scripts" element={<AdminGuard><ScriptManager /></AdminGuard>} />
                      
                      {/* New Admin Panel Routes */}
                      <Route path="/admin/hub" element={<AdminGuard><AdminHub /></AdminGuard>} />
                      <Route path="/admin/topics/:topicId/revisions" element={<AdminGuard><AdminTopicRevisionsPage /></AdminGuard>} />
                      <Route path="/admin/reviewer-queue" element={<AdminGuard><AdminReviewerQueuePage /></AdminGuard>} />
                      <Route path="/admin/social-hub" element={<AdminGuard><AdminSocialHubPage /></AdminGuard>} />
                      <Route path="/admin/media-deploy" element={<AdminGuard><AdminMediaDeployPage /></AdminGuard>} />
                      <Route path="/admin/academy-pricing" element={<AdminGuard><AdminAcademyModulePricingPage /></AdminGuard>} />
                      <Route path="/admin/pillars" element={<AdminGuard><AdminPillarsPage /></AdminGuard>} />
                      <Route path="/admin/transcripts" element={<AdminGuard><AdminTranscriptsPage /></AdminGuard>} />
                      <Route path="/admin/translation-graph" element={<AdminGuard><AdminTMKGPage /></AdminGuard>} />
                      <Route path="/admin/living-drafts" element={<AdminGuard><LivingDraftsProvenancePage /></AdminGuard>} />
                      <Route path="/admin/sip" element={<AdminGuard><SipDashboardPage /></AdminGuard>} />
                      <Route path="/ai/coach" element={<SleepCoachPage />} />
                      <Route path="/pillars" element={<PillarsIndexPage />} />
                      <Route path="/pillars/:slug" element={<PillarPage />} />
                      <Route path="/admin/db-health" element={<AdminGuard><AdminDBHealthPage /></AdminGuard>} />
                      <Route path="/admin/analytics/bilingual-reach" element={<AdminGuard><BilingualReachPage /></AdminGuard>} />
                      <Route path="/admin/institutional-leads" element={<AdminGuard><InstitutionalLeadsPage /></AdminGuard>} />
                      <Route path="/admin/institutional-licensing" element={<AdminGuard><AdminInstitutionalLicensingPage /></AdminGuard>} />
                      <Route path="/institution/licenses" element={<OrgLicensesPage />} />
                      <Route path="/admin/institutional-licensing/pricing" element={<AdminGuard><InstitutionalPricingPreviewPage /></AdminGuard>} />
                      <Route path="/institutional-education" element={<InstitutionalEducationPage />} />
                      <Route path="/admin/organizational-leads" element={<AdminGuard><OrganizationalLeadsPage /></AdminGuard>} />

                      {/* Phase B · Expansion Program */}
                      <Route path="/videos/institutional" element={<InstitutionalVideosPage />} />
                      <Route path="/about-shs" element={<InstitutionalVideosPage />} />
                      <Route path="/admin/video-studio" element={<AdminGuard><AdminVideoStudioPage /></AdminGuard>} />
                      <Route path="/admin/video-studio/phase-d1-review" element={<AdminGuard><AdminPhaseD1ReviewPage /></AdminGuard>} />
                      <Route path="/admin/video-studio/permanent-library" element={<AdminGuard><AdminPermanentLibraryPage /></AdminGuard>} />
                      <Route path="/admin/video-studio/founder-library" element={<AdminGuard><AdminFounderLibraryPage /></AdminGuard>} />
                      <Route path="/admin/video-studio/dub-pilot" element={<AdminGuard><AdminDubPilotPage /></AdminGuard>} />
                      <Route path="/admin/video-studio/social-publishing" element={<AdminGuard><AdminSocialPublishingPage /></AdminGuard>} />
                      <Route path="/admin/video-studio/v1-parity" element={<AdminGuard><AdminV1ParityPage /></AdminGuard>} />
                      <Route path="/admin/founder-assets" element={<AdminGuard><AdminFounderAssetsPage /></AdminGuard>} />
                      <Route path="/admin/institutional-videos" element={<AdminGuard><AdminNav current="inst"><AdminInstitutionalVideosPage /></AdminNav></AdminGuard>} />
                      <Route path="/admin/caption-review" element={<AdminGuard><AdminNav current="inst"><AdminCaptionReviewPage /></AdminNav></AdminGuard>} />
                      <Route path="/admin/institutional-classifier" element={<AdminGuard><AdminInstitutionalClassifierPage /></AdminGuard>} />
                      <Route path="/clinics/private-pricing" element={<Navigate to="/" replace />} />
                      <Route path="/organizations/pricing" element={<OrganizationalPricingPage />} />
                      <Route path="/legal/privacy" element={<PrivacyPolicyV2Page />} />
                      <Route path="/legal/privacy-v2" element={<PrivacyPolicyV2Page />} />
                      <Route path="/legal/sub-processors" element={<SubProcessorsPage />} />
                      <Route path="/admin/video-production" element={<AdminGuard><VideoProductionDashboard /></AdminGuard>} />
                      <Route path="/admin/videos" element={<AdminGuard><AdminVideos /></AdminGuard>} />
                      <Route path="/admin/videos/import" element={<AdminGuard><BulkVideoImport /></AdminGuard>} />
                      <Route path="/admin/academy" element={<AdminGuard><AdminAcademy /></AdminGuard>} />
                      <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
                      <Route path="/admin/enterprise" element={<AdminGuard><AdminEnterprise /></AdminGuard>} />
                      <Route path="/admin/series" element={<AdminGuard><AdminSeries /></AdminGuard>} />
                      
                      {/* SHS AI Growth Engine™ (Admin Marketing) */}
                      <Route path="/admin/growth-engine" element={<AdminGuard><GrowthEnginePage /></AdminGuard>} />
                      
                      {/* Revenue Dashboard (Executive Analytics) */}
                      <Route path="/admin/revenue" element={<AdminGuard><RevenueDashboardPage /></AdminGuard>} />
                      
                      {/* Organizations (B2B) */}
                      <Route path="/organizations" element={<OrganizationsPage />} />
                      
                      {/* CPAP Fit Assistant™ */}
                      <Route path="/cpap-fit" element={<CPAPFitAssistantPage />} />
                      <Route path="/admin/cpap-analytics" element={<AdminGuard><CPAPAnalyticsPage /></AdminGuard>} />
                      
                      {/* CPAP Adherence Engine™ */}
                      <Route path="/cpap-adherence" element={<CPAPAdherencePage />} />
                      <Route path="/cpap-stories" element={<CPAPSuccessStoriesPage />} />
                      <Route path="/admin/cpap-stories" element={<AdminGuard><AdminCPAPStoriesPage /></AdminGuard>} />
                      <Route path="/admin/ecosystem" element={<AdminGuard><AdminEcosystemPage /></AdminGuard>} />
                      
                      {/* Ad Campaign Manager */}
                      <Route path="/admin/campaigns" element={<AdminGuard><CampaignDashboard /></AdminGuard>} />
                      
                      {/* Video Script Generator */}
                      <Route path="/admin/video-scripts" element={<AdminGuard><VideoScriptGeneratorPage /></AdminGuard>} />
                      
                      {/* Growth Command Center (Admin) */}
                      <Route path="/admin/growth" element={<AdminGuard><GrowthCommandCenterPage /></AdminGuard>} />
                      
                      {/* CEO Dashboard (Admin) */}
                      <Route path="/admin/ceo-dashboard" element={<AdminGuard><AdminGrowthDashboard /></AdminGuard>} />
                      
                      {/* Content Protection Admin */}
                      <Route path="/admin/protection" element={<AdminGuard><ContentProtectionAdminPage /></AdminGuard>} />
                      
                      {/* Research Program */}
                      <Route path="/research" element={<ResearchPage />} />
                      <Route path="/admin/research" element={<AdminGuard><AdminResearch /></AdminGuard>} />
                      
                      {/* B2B Clinic Routes — DISABLED (Pre-Launch Legal & Regulatory directive, Feb 2026).
                         The entire /clinics, /portal, /intake, and /report-assistant verticals are
                         retired from public access. SHS launches strictly as an educational, wellness,
                         training, and compliance-support platform. See:
                         /app/memory/PRE_LAUNCH_LEGAL_AND_REGULATORY_READINESS_REPORT.md */}
                      <Route path="/clinics" element={<Navigate to="/" replace />} />
                      <Route path="/clinics/pricing" element={<Navigate to="/" replace />} />
                      <Route path="/clinics/login" element={<Navigate to="/" replace />} />
                      <Route path="/clinics/register" element={<Navigate to="/" replace />} />
                      <Route path="/clinics/private-pricing" element={<Navigate to="/" replace />} />
                      <Route path="/portal" element={<Navigate to="/" replace />} />
                      <Route path="/portal/team" element={<Navigate to="/" replace />} />
                      <Route path="/intake/:token" element={<Navigate to="/" replace />} />
                      <Route path="/intake/:token/consent" element={<Navigate to="/" replace />} />
                      <Route path="/intake/:token/demographics" element={<Navigate to="/" replace />} />
                      <Route path="/intake/:token/questionnaires" element={<Navigate to="/" replace />} />
                      <Route path="/intake/:token/results" element={<Navigate to="/" replace />} />
                      <Route path="/report-assistant" element={<Navigate to="/" replace />} />
                      <Route path="/report-assistant/new" element={<Navigate to="/" replace />} />
                      <Route path="/report-assistant/templates" element={<Navigate to="/" replace />} />
                      <Route path="/report-assistant/:draftId" element={<Navigate to="/" replace />} />
                      
                      {/* Strategic Concept Pages (Unlisted - noindex) */}
                      <Route path="/scoring-mastery" element={<ScoringMasteryPage />} />
                      <Route path="/cbti-coach" element={<CbtiCoachPage />} />
                      
                      {/* Tech Pocket Guide™ Routes */}
                      <Route path="/tech-pocket-guide" element={<TechPocketGuideLandingPage />} />
                      <Route path="/tech-pocket-guide/app" element={<TechPocketGuideApp />} />
                      
                      {/* Scoring Mastery™ MVP App Routes */}
                      <Route path="/scoring-mastery/app" element={<ScoringMasteryAppPage />} />
                      <Route path="/scoring-mastery/app/exercise/:exerciseId" element={<ScoringMasteryExercisePage />} />
                      
                      {/* SHS Scoring Simulator™ Routes */}
                      <Route path="/scoring-simulator" element={<ScoringSimulatorPage />} />
                      
                      {/* Admin Scoring Mastery Routes */}
                      <Route path="/admin/scoring-mastery" element={<AdminGuard><AdminScoringMastery /></AdminGuard>} />
                      <Route path="/admin/scoring-mastery/app" element={<AdminGuard><ScoringMasteryAdminPage /></AdminGuard>} />
                      <Route path="/admin/scoring-mastery/qa" element={<AdminGuard><ScoringMasteryQADashboardPage /></AdminGuard>} />
                      
                      {/* SHS Sleep Trends & Insights™ (Internal) */}
                      <Route path="/admin/insights" element={<AdminGuard><InsightsDashboardPage /></AdminGuard>} />
                      
                      {/* Payment Routes */}
                      <Route path="/payment/success" element={<PaymentSuccessPage />} />
                      <Route path="/payment/cancel" element={<PaymentCancelPage />} />

                      {/* Catch-all — visible diagnostic instead of a mystery blank page.
                          A missing route on production usually means the deployed bundle is
                          stale relative to the source (a new route was merged but not yet
                          shipped). Show a clear message and refresh guidance. */}
                      <Route path="/pricing" element={<KeepQueryRedirect to="/membership" />} />
                      <Route path="/memberships" element={<KeepQueryRedirect to="/membership" />} />
                      <Route path="/sleep-reset-academy" element={<KeepQueryRedirect to="/academy/sleep-reset" />} />
                      <Route path="/reset-password" element={<KeepQueryRedirect to="/education/reset-password" />} />
                      <Route path="/cpap-fit-assistant" element={<KeepQueryRedirect to="/cpap-fit" />} />
                      <Route path="/ai-assistant" element={<KeepQueryRedirect to="/ai" />} />
                      <Route path="*" element={<CatchAllNotFound />} />
                  </Routes>
                  </GoogleAuthGate>
                  </Suspense>
                  </main>
                  <Footer />
                  <AIHelper />
                  <Toaster />
                  <PWAInstallPrompt />
                  <HelpCenterLite />
                  <FloatingAudioPlayer />
                  <MobileAssistantHub />
                  <CookieNoticeBanner />
                  <GuestDataPrompt />
                  <FounderPreviewBanner />
                    </AudioPlayerProvider>
                </AcademyProvider>
              </EducationAuthProvider>
            </ClinicAuthProvider>
          </AIChatProvider>
        </LanguageProvider>
        </MotionModeProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
